import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { config } from '../config';
import './NewsletterList.css';

// Toggle this to switch between mock data (true) and real backend (false)
const USE_MOCK_DATA = true;

interface Newsletter {
  id: string;
  htmlUrl: string;
  textUrl: string;
  pdfUrl: string;
  created: string;
  isProd: boolean;
  metadata?: {
    title?: string;
    date?: string;
    previewImageUrl?: string;
    websiteUrl?: string;
    linkedinUrl?: string;
    facebookUrl?: string;
    instagramUrl?: string;
    trackingV2?: number;
  };
}

interface MailingListEmail {
  email: string;
  status: string;
  lastUpdated: string;
  createdAt?: string;
  mailingLists?: string[];
  engagementStats?: {
    totalOpened: number;
    totalClicked: number;
    totalNewsletters: number;
  };
  sentNewsletters?: string[];
}

interface MailingList {
  id: string;
  name: string;
  subscriberCount: number;
  lastUpdated: string;
  emails?: MailingListEmail[];
}

interface AnalyticsData {
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  avgOpenRate: number;
  avgClickRate: number;
  recentActivity: number;
}

interface NewsletterAnalytics {
  newsletterId: string;
  stats: {
    total: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    viewed: number;
    bounced: number;
    complaints: number;
    openRate: string;
    clickRate: string;
    deliveryRate: string;
    bounceRate: string;
  };
  recipients: any[];
  totalRecipients: number;
}

interface CompanyInfo {
  name: string;
  logo?: string;
  initials: string;
  theme?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
}

const NewsletterList: React.FC = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [mailingLists, setMailingLists] = useState<MailingList[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    name: 'Newsletters',
    initials: 'NL',
    theme: {
      primaryColor: '#4f46e5',
      secondaryColor: '#4338ca'
    }
  });
  const [currentUser, setCurrentUser] = useState<{name?: string, email?: string} | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'overview' | 'mailing-lists' | 'newsletters'>('overview');
  const [selectedMailingList, setSelectedMailingList] = useState<MailingList | null>(null);
  const [showSubscriberModal, setShowSubscriberModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'subscribed' | 'unsubscribed' | 'pending'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'email' | 'status' | 'lastUpdated' | 'engagement'>('email');
  const [pendingStatusChanges, setPendingStatusChanges] = useState<Map<string, string>>(new Map());
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [filterMailingList, setFilterMailingList] = useState<string>('all');
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showIntegrationCode, setShowIntegrationCode] = useState(false);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [showManageListsModal, setShowManageListsModal] = useState(false);
  const [selectedListAction, setSelectedListAction] = useState<'add' | 'remove'>('add');
  const [targetMailingList, setTargetMailingList] = useState<string>('');
  const [showAddSubscriberModal, setShowAddSubscriberModal] = useState(false);
  const [newSubscriberEmail, setNewSubscriberEmail] = useState('');
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error' | 'info'} | null>(null);
  const [isAddingSubscriber, setIsAddingSubscriber] = useState(false);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [isManagingLists, setIsManagingLists] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedNewsletterAnalytics, setSelectedNewsletterAnalytics] = useState<NewsletterAnalytics | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [showHtmlPreviewModal, setShowHtmlPreviewModal] = useState(false);
  const [previewHtmlContent, setPreviewHtmlContent] = useState<string>('');
  const [loadingPreviewIds, setLoadingPreviewIds] = useState<Set<string>>(new Set());

  // Load company and user info (simulated for now)
  useEffect(() => {
    // Simulate loading company info based on authentication
    const loadCompanyInfo = () => {
      // In a real app, this would come from authentication context
      const companies = {
        'darlingmuesco': {
          name: 'Darling Muesco',
          initials: 'DM',
          theme: { primaryColor: '#4f46e5', secondaryColor: '#4338ca' }
        },
        'techcorp': {
          name: 'TechCorp Inc',
          initials: 'TC',
          theme: { primaryColor: '#059669', secondaryColor: '#047857' }
        },
        'startup': {
          name: 'Startup Co',
          initials: 'SC',
          theme: { primaryColor: '#dc2626', secondaryColor: '#b91c1c' }
        }
      };

      // Default to first company or detect from URL/auth
      const currentCompany = companies['darlingmuesco'] || {
        name: 'Newsletters',
        initials: 'NL',
        theme: { primaryColor: '#4f46e5', secondaryColor: '#4338ca' }
      };

      setCompanyInfo(currentCompany);

      // Simulate user info
      setCurrentUser({
        name: 'John Doe',
        email: 'john@' + currentCompany.name.toLowerCase().replace(/\s+/g, '') + '.com'
      });
    };

    loadCompanyInfo();
  }, []);

  useEffect(() => {
    // Load mock data instead of API calls
    fetchNewsletters();
    fetchMailingLists();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ message, type });
  };

  const fetchNewsletters = async () => {
    try {
      if (USE_MOCK_DATA) {
        // Mock data - API not available yet
        const mockNewsletters: Newsletter[] = [];
        setNewsletters(mockNewsletters);
      } else {
        const data = await apiService.getNewsletters();
        setNewsletters(Array.isArray(data) ? data : data.newsletters || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch newsletters');
    }
  };

  const fetchMailingLists = async () => {
    try {
      if (USE_MOCK_DATA) {
        // Mock data - API not available yet
        const mockEmails: MailingListEmail[] = [];

        const defaultList: MailingList = {
          id: 'default',
          name: 'Default Mailing List',
          subscriberCount: 0,
          lastUpdated: new Date().toISOString(),
          emails: mockEmails
        };

        setMailingLists([defaultList]);
      } else {
        const response = await apiService.getMailingLists();
        if (response && response.emails) {
          const emailList = response.emails;
          const subscribedCount = emailList.filter((email: MailingListEmail) =>
            email.status === 'SUBSCRIBED'
          ).length;

          const defaultList: MailingList = {
            id: 'default',
            name: 'Default Mailing List',
            subscriberCount: subscribedCount,
            lastUpdated: emailList.length > 0 ? emailList[0].lastUpdated : new Date().toISOString(),
            emails: emailList
          };

          setMailingLists([defaultList]);
        } else {
          setMailingLists([]);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch mailing lists:', err);
      setMailingLists([]);
    }
  };

  const fetchAnalytics = async () => {
    try {
      // Mock data for now - replace with actual API call
      setAnalytics({
        totalSent: 15420,
        totalOpens: 8934,
        totalClicks: 2341,
        avgOpenRate: 58.2,
        avgClickRate: 15.2,
        recentActivity: 234
      });
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setLoading(false);
    }
  };

  const handleDeleteNewsletter = async (id: string, isProd: boolean) => {
    if (USE_MOCK_DATA) {
      showNotification('Newsletter backend not yet available. UI-only mode.', 'info');
      return;
    }

    if (isProd) {
      showNotification('Production newsletters cannot be deleted from this interface', 'error');
      return;
    }

    try {
      await apiService.deleteNewsletter(id);
      fetchNewsletters();
      showNotification('Newsletter deleted successfully', 'success');
    } catch (err: any) {
      showNotification('Failed to delete newsletter: ' + err.message, 'error');
    }
  };

  const handleViewAnalytics = async (newsletterId: string) => {
    if (USE_MOCK_DATA) {
      showNotification('Analytics backend not yet available. UI-only mode.', 'info');
      return;
    }

    setIsLoadingAnalytics(true);
    try {
      const analyticsData = await apiService.getNewsletterAnalytics(newsletterId);
      setSelectedNewsletterAnalytics(analyticsData);
      setShowAnalyticsModal(true);
    } catch (err: any) {
      showNotification('Failed to fetch analytics: ' + err.message, 'error');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const handleViewHtml = async (newsletterId: string) => {
    if (USE_MOCK_DATA) {
      showNotification('HTML preview backend not yet available. UI-only mode.', 'info');
      return;
    }

    setLoadingPreviewIds(prev => new Set(prev).add(newsletterId));
    try {
      const previewData = await apiService.getNewsletterHtmlPreview(newsletterId, 'darlingmuesco');
      setPreviewHtmlContent(previewData.html);
      setShowHtmlPreviewModal(true);
    } catch (err: any) {
      showNotification('Failed to load HTML preview: ' + err.message, 'error');
    } finally {
      setLoadingPreviewIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(newsletterId);
        return newSet;
      });
    }
  };


  const handleViewSubscribers = (list: MailingList) => {
    setSelectedMailingList(list);
    setShowSubscriberModal(true);
  };

  const handleCloseSubscriberModal = () => {
    setShowSubscriberModal(false);
    setSelectedMailingList(null);
  };

  const handleUnsubscribeEmail = async (email: string) => {
    if (USE_MOCK_DATA) {
      showNotification('Subscriber management backend not yet available. UI-only mode.', 'info');
      return;
    }

    try {
      console.log(`Unsubscribing email: ${email}`);
      await fetchMailingLists();
      setSelectedMailingList(null);
      setShowSubscriberModal(false);
      showNotification(`${email} has been unsubscribed`, 'success');
    } catch (err: any) {
      showNotification('Failed to unsubscribe email: ' + err.message, 'error');
    }
  };

  const handleAddSubscriber = async () => {
    if (USE_MOCK_DATA) {
      showNotification('Add subscriber backend not yet available. UI-only mode.', 'info');
      setIsAddingSubscriber(false);
      return;
    }

    if (!newSubscriberEmail || !newSubscriberEmail.includes('@')) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    if (isAddingSubscriber) return;

    setIsAddingSubscriber(true);
    try {
      showNotification(`Adding ${newSubscriberEmail}...`, 'info');

      await apiService.callBackendOperation('subscribe', {
        email: newSubscriberEmail,
        mailingList: 'default'
      });

      setShowAddSubscriberModal(false);
      setNewSubscriberEmail('');

      await fetchMailingLists();
      showNotification(`Successfully added ${newSubscriberEmail} to mailing list`, 'success');
    } catch (err: any) {
      showNotification('Failed to add subscriber: ' + err.message, 'error');
    } finally {
      setIsAddingSubscriber(false);
    }
  };

  const handleResubscribeEmail = async (email: string) => {
    if (USE_MOCK_DATA) {
      showNotification('Resubscribe backend not yet available. UI-only mode.', 'info');
      return;
    }

    try {
      console.log(`Resubscribing email: ${email}`);
      await fetchMailingLists();
      showNotification(`${email} has been resubscribed`, 'success');
    } catch (err: any) {
      showNotification('Failed to resubscribe email: ' + err.message, 'error');
    }
  };


  const handleSelectEmail = (email: string) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedEmails(newSelected);
  };

  const handleSelectAll = () => {
    if (!mailingLists[0]?.emails) return;
    
    const filteredEmails = mailingLists[0].emails
      .filter(email => {
        const matchesSearch = email.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || 
          email.status.toLowerCase() === filterStatus.toLowerCase();
        return matchesSearch && matchesFilter;
      })
      .map(e => e.email);
    
    if (selectedEmails.size === filteredEmails.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(filteredEmails));
    }
  };

  const handleBatchStatusChange = (newStatus: 'SUBSCRIBED' | 'UNSUBSCRIBED') => {
    const newChanges = new Map(pendingStatusChanges);
    selectedEmails.forEach(email => {
      newChanges.set(email, newStatus);
    });
    setPendingStatusChanges(newChanges);
    setSelectedEmails(new Set());
  };

  const handleConfirmChanges = async () => {
    if (USE_MOCK_DATA) {
      showNotification('Batch update backend not yet available. UI-only mode.', 'info');
      setIsApplyingChanges(false);
      return;
    }

    if (pendingStatusChanges.size === 0) return;
    if (isApplyingChanges) return;

    setIsApplyingChanges(true);
    try {
      showNotification(`Applying ${pendingStatusChanges.size} status changes...`, 'info');

      const updates = Array.from(pendingStatusChanges.entries()).map(([email, status]) => ({
        email,
        status: status as 'SUBSCRIBED' | 'UNSUBSCRIBED'
      }));

      await apiService.batchUpdateSubscriberStatus(updates);

      setPendingStatusChanges(new Map());
      await fetchMailingLists();
      showNotification('Status changes applied successfully!', 'success');
    } catch (err: any) {
      showNotification('Failed to apply status changes: ' + err.message, 'error');
    } finally {
      setIsApplyingChanges(false);
    }
  };

  const handleCancelChanges = () => {
    if (pendingStatusChanges.size > 0) {
      setPendingStatusChanges(new Map());
      showNotification('Pending changes discarded', 'info');
    }
  };

  const handleCreateMailingList = async () => {
    if (USE_MOCK_DATA) {
      showNotification('Create mailing list backend not yet available. UI-only mode.', 'info');
      setIsCreatingList(false);
      return;
    }

    if (isCreatingList) return;

    if (!newListName.trim()) {
      showNotification('Please enter a mailing list name', 'error');
      return;
    }

    if (selectedEmails.size === 0) {
      showNotification('Please select at least one email address', 'error');
      return;
    }

    const existingLists = new Set();
    mailingLists[0]?.emails?.forEach(email => {
      email.mailingLists?.forEach(list => existingLists.add(list));
    });

    if (existingLists.has(newListName.trim())) {
      showNotification(`Mailing list "${newListName}" already exists. Please choose a different name.`, 'error');
      return;
    }

    setIsCreatingList(true);
    try {
      showNotification(`Creating mailing list "${newListName}" with ${selectedEmails.size} emails...`, 'info');

      const emailsArray = Array.from(selectedEmails);
      for (const email of emailsArray) {
        const emailRecord = mailingLists[0].emails?.find(e => e.email === email);
        const currentLists = emailRecord?.mailingLists || ['default'];

        if (!currentLists.includes(newListName)) {
          const updatedLists = [...currentLists, newListName];

          await apiService.callBackendOperation('update-user-mailing-lists', {
            email: email,
            mailingLists: updatedLists,
            isTestSend: false
          });
        }
      }

      setShowCreateListModal(false);
      setNewListName('');
      setSelectedEmails(new Set());

      await fetchMailingLists();
      showNotification(`Created mailing list "${newListName}" with ${emailsArray.length} subscribers`, 'success');
    } catch (error: any) {
      showNotification('Failed to create mailing list: ' + error.message, 'error');
    } finally {
      setIsCreatingList(false);
    }
  };

  const getDisplayStatus = (email: string, currentStatus: string) => {
    return pendingStatusChanges.get(email) || currentStatus;
  };

  const handleManageMailingLists = async () => {
    if (USE_MOCK_DATA) {
      showNotification('Manage mailing lists backend not yet available. UI-only mode.', 'info');
      setIsManagingLists(false);
      return;
    }

    if (!targetMailingList) {
      showNotification('Please select a mailing list', 'error');
      return;
    }

    if (selectedEmails.size === 0) {
      showNotification('No emails selected', 'error');
      return;
    }

    if (isManagingLists) return;

    const emailsArray = Array.from(selectedEmails);
    const actionText = selectedListAction === 'add' ? 'Adding' : 'Removing';

    setIsManagingLists(true);
    try {
      showNotification(`${actionText} ${emailsArray.length} email(s) ${selectedListAction === 'add' ? 'to' : 'from'} "${targetMailingList}"...`, 'info');

      for (const email of emailsArray) {
        const emailRecord = mailingLists[0].emails?.find(e => e.email === email);
        const currentLists = emailRecord?.mailingLists || ['default'];

        let updatedLists: string[];
        if (selectedListAction === 'add') {
          if (!currentLists.includes(targetMailingList)) {
            updatedLists = [...currentLists, targetMailingList];
          } else {
            continue;
          }
        } else {
          updatedLists = currentLists.filter(list => list !== targetMailingList);
          if (updatedLists.length === 0) {
            updatedLists = ['default'];
          }
        }

        await apiService.callBackendOperation('update-user-mailing-lists', {
          email: email,
          mailingLists: updatedLists,
          isTestSend: false
        });
      }

      setShowManageListsModal(false);
      setTargetMailingList('');
      setSelectedEmails(new Set());

      await fetchMailingLists();

      const completedActionText = selectedListAction === 'add' ? 'added to' : 'removed from';
      showNotification(`Successfully ${completedActionText} "${targetMailingList}" list`, 'success');
    } catch (error: any) {
      showNotification('Failed to update mailing lists: ' + error.message, 'error');
    } finally {
      setIsManagingLists(false);
    }
  };

  const productionNewsletters = newsletters.filter(n => n.isProd);
  const draftNewsletters = newsletters.filter(n => !n.isProd);

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="dashboard-container">
      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          <span>{notification.message}</span>
          <button className="notification-close" onClick={() => setNotification(null)}>×</button>
        </div>
      )}
      {/* Modern Header with Dynamic Branding */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-title">
            <div className="company-logo">
              {companyInfo.logo ? (
                <img src={companyInfo.logo} alt={companyInfo.name} />
              ) : (
                <span>{companyInfo.initials}</span>
              )}
            </div>
            <div>
              <h1>{companyInfo.name}</h1>
              <div className="company-subtitle">Newsletter Management Platform</div>
            </div>
          </div>
          <div className="header-actions">
            {currentUser && (
              <div className="user-profile">
                <div className="user-avatar">
                  {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="user-info">
                  <div className="text-sm font-semibold">{currentUser.name || 'User'}</div>
                  <div className="text-xs opacity-75">{currentUser.email || ''}</div>
                </div>
              </div>
            )}
            {activeSection === 'newsletters' && (
              <button className="btn btn-primary" onClick={() => showNotification('PDF Newsletter creation coming soon!', 'info')}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Newsletter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modern Navigation Tabs */}
      <div className="dashboard-nav">
        <div className="nav-container">
          <button
            className={`nav-tab ${activeSection === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveSection('overview')}
          >
            Overview
            <span className="tab-badge">{analytics ? '📊' : ''}</span>
          </button>
          <button
            className={`nav-tab ${activeSection === 'mailing-lists' ? 'active' : ''}`}
            onClick={() => setActiveSection('mailing-lists')}
          >
            Subscribers
            <span className="tab-badge">{mailingLists.reduce((total, list) => total + (list.subscriberCount || 0), 0)}</span>
          </button>
          <button
            className={`nav-tab ${activeSection === 'newsletters' ? 'active' : ''}`}
            onClick={() => setActiveSection('newsletters')}
          >
            Newsletters
            <span className="tab-badge">{newsletters.length}</span>
          </button>
        </div>
      </div>

      {/* Content Sections */}
      {activeSection === 'overview' && (
        <div className="section-container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Analytics Overview</h2>
              <p className="section-description">Track your newsletter performance and engagement metrics</p>
            </div>
            <div className="section-actions">
              <button className="btn btn-secondary" onClick={() => {
                fetchNewsletters();
                fetchMailingLists();
                fetchAnalytics();
              }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          {/* Analytics Cards */}
          {analytics && (
            <div className="analytics-grid">
              <div className="analytics-card">
                <h3>Total Sent</h3>
                <div className="analytics-value">{analytics.totalSent.toLocaleString()}</div>
                <div className="analytics-trend trend-up">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Emails Delivered
                </div>
              </div>
              <div className="analytics-card">
                <h3>Open Rate</h3>
                <div className="analytics-value">{analytics.avgOpenRate}%</div>
                <div className="analytics-trend trend-up">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  {analytics.totalOpens.toLocaleString()} opens
                </div>
              </div>
              <div className="analytics-card">
                <h3>Click Rate</h3>
                <div className="analytics-value">{analytics.avgClickRate}%</div>
                <div className="analytics-trend trend-up">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  {analytics.totalClicks.toLocaleString()} clicks
                </div>
              </div>
              <div className="analytics-card">
                <h3>Subscribers</h3>
                <div className="analytics-value">{mailingLists.reduce((total, list) => total + (list.subscriberCount || 0), 0).toLocaleString()}</div>
                <div className="analytics-trend trend-up">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Active subscribers
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="quick-stats">
            <div className="stat-item">
              <span className="stat-number">{productionNewsletters.length}</span>
              <span className="stat-label">Production Newsletters</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{draftNewsletters.length}</span>
              <span className="stat-label">Draft Newsletters</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{mailingLists.length}</span>
              <span className="stat-label">Mailing Lists</span>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'mailing-lists' && (
        <div className="section-container">
          <div className="section-header">
            <h2>Mailing List Management</h2>
            <div className="section-actions">
              <button className="btn btn-primary" onClick={() => setShowAddSubscriberModal(true)}>
                <span style={{fontSize: '1.2em'}}>+</span> Add Subscriber
              </button>
              <button className="btn btn-secondary" onClick={() => showNotification('Export functionality coming soon!', 'info')}>
                📥 Export All
              </button>
              <button className="btn btn-info" onClick={() => setShowIntegrationCode(!showIntegrationCode)}>
                {'</>'} Integration Code
              </button>
            </div>
          </div>

          {/* Mailing List Stats Overview */}
          {mailingLists.length > 0 && mailingLists[0].emails && (
            <div className="mailing-list-stats">
              <div className="stat-card">
                <div className="stat-value">{mailingLists[0].emails.length}</div>
                <div className="stat-label">Total Emails</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {mailingLists[0].emails.filter(e => e.status === 'SUBSCRIBED').length}
                </div>
                <div className="stat-label">Active Subscribers</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {mailingLists[0].emails.filter(e => e.status === 'UNSUBSCRIBED').length}
                </div>
                <div className="stat-label">Unsubscribed</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {mailingLists[0].emails.filter(e => e.status === 'PENDING').length}
                </div>
                <div className="stat-label">Pending</div>
              </div>
            </div>
          )}

          {/* Integration Code Section */}
          {showIntegrationCode && (
            <div className="integration-code-section">
              <h3>Website Integration</h3>
              <p>Add this JavaScript code to your website to allow users to subscribe:</p>
              <div className="code-block">
                <pre><code>{`// Add this function to your website
async function subscribeToNewsletter(email, mailingList = 'default') {
  try {
    const response = await fetch('${config.API_BASE_URL}', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        identifier: 'subscribe',
        email: email,
        mailingList: mailingList
      })
    });
    
    if (response.ok) {
      const result = await response.json();
      alert('Successfully subscribed!');
    } else {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Subscription failed');
    }
  } catch (error) {
    alert('Error: ' + error.message);
  }
}

// Example usage in a form
document.getElementById('subscribe-btn').addEventListener('click', () => {
  const email = document.getElementById('email-input').value;
  subscribeToNewsletter(email);
});`}</code></pre>
              </div>
              <p><strong>HTML Example:</strong></p>
              <div className="code-block">
                <pre><code>{`<form id="newsletter-form">
  <input type="email" id="email-input" placeholder="Enter your email" required>
  <button type="button" id="subscribe-btn">Subscribe</button>
</form>`}</code></pre>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div className="mailing-list-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-controls">
              <select 
                value={filterMailingList} 
                onChange={(e) => setFilterMailingList(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Mailing Lists</option>
                {/* Dynamically populate mailing lists */}
                {Array.from(new Set(
                  mailingLists[0]?.emails?.flatMap(email => email.mailingLists || []) || []
                )).sort().map(listName => (
                  <option key={listName} value={listName}>{listName}</option>
                ))}
              </select>
              <select 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="subscribed">Subscribed</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="pending">Pending</option>
              </select>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value as any)}
                className="filter-select"
              >
                <option value="email">Sort by Email</option>
                <option value="status">Sort by Status</option>
                <option value="lastUpdated">Sort by Last Updated</option>
                <option value="engagement">Sort by Engagement</option>
              </select>
            </div>
          </div>

          {/* Batch Actions Bar */}
          {selectedEmails.size > 0 && (
            <div className="batch-actions-bar">
              <span className="selected-count">{selectedEmails.size} selected</span>
              <button 
                className="btn btn-sm btn-success"
                onClick={() => handleBatchStatusChange('SUBSCRIBED')}
              >
                Set as Subscribed
              </button>
              <button 
                className="btn btn-sm btn-warning"
                onClick={() => handleBatchStatusChange('UNSUBSCRIBED')}
              >
                Set as Unsubscribed
              </button>
              <button 
                className="btn btn-sm btn-info"
                onClick={() => {
                  setSelectedListAction('add');
                  setShowManageListsModal(true);
                }}
              >
                Add to List
              </button>
              <button 
                className="btn btn-sm btn-danger"
                onClick={() => {
                  setSelectedListAction('remove');
                  setShowManageListsModal(true);
                }}
              >
                Remove from List
              </button>
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => setShowCreateListModal(true)}
              >
                Create List
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedEmails(new Set())}
              >
                Clear Selection
              </button>
            </div>
          )}

          {/* Pending Changes Confirmation Bar */}
          {pendingStatusChanges.size > 0 && (
            <div className="pending-changes-bar">
              <div className="pending-info">
                <span className="pending-count">{pendingStatusChanges.size} pending status changes</span>
                <span className="pending-details">
                  ({Array.from(pendingStatusChanges.values()).filter(s => s === 'SUBSCRIBED').length} to subscribe, 
                  {Array.from(pendingStatusChanges.values()).filter(s => s === 'UNSUBSCRIBED').length} to unsubscribe)
                </span>
              </div>
              <div className="pending-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleConfirmChanges}
                  disabled={isApplyingChanges}
                >
                  {isApplyingChanges ? 'Applying...' : 'Apply Changes'}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={handleCancelChanges}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Subscriber Table */}
          {mailingLists.length > 0 && mailingLists[0].emails ? (
            <div className="subscriber-table-container">
              <table className="subscriber-table-full">
                <thead>
                  <tr>
                    <th>
                      <input 
                        type="checkbox"
                        checked={selectedEmails.size > 0 && mailingLists[0].emails?.filter(email => {
                          const matchesSearch = email.email.toLowerCase().includes(searchTerm.toLowerCase());
                          const matchesFilter = filterStatus === 'all' || 
                            email.status.toLowerCase() === filterStatus.toLowerCase();
                          return matchesSearch && matchesFilter;
                        }).length === selectedEmails.size}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Mailing Lists</th>
                    <th>Created</th>
                    <th>Last Updated</th>
                    <th>Opens</th>
                    <th>Clicks</th>
                    <th>Sent</th>
                    <th>Engagement Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {mailingLists[0].emails
                    .filter(email => {
                      const matchesSearch = email.email.toLowerCase().includes(searchTerm.toLowerCase());
                      const matchesFilter = filterStatus === 'all' || 
                        email.status.toLowerCase() === filterStatus.toLowerCase();
                      const matchesMailingList = filterMailingList === 'all' || 
                        (email.mailingLists && email.mailingLists.includes(filterMailingList));
                      return matchesSearch && matchesFilter && matchesMailingList;
                    })
                    .sort((a, b) => {
                      switch(sortBy) {
                        case 'email':
                          return a.email.localeCompare(b.email);
                        case 'status':
                          return a.status.localeCompare(b.status);
                        case 'lastUpdated':
                          return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
                        case 'engagement':
                          const aRate = a.engagementStats ? 
                            (a.engagementStats.totalOpened / Math.max(a.engagementStats.totalNewsletters, 1)) : 0;
                          const bRate = b.engagementStats ? 
                            (b.engagementStats.totalOpened / Math.max(b.engagementStats.totalNewsletters, 1)) : 0;
                          return bRate - aRate;
                        default:
                          return 0;
                      }
                    })
                    .map((email, index) => {
                      const engagementRate = email.engagementStats && email.engagementStats.totalNewsletters > 0
                        ? ((email.engagementStats.totalOpened / email.engagementStats.totalNewsletters) * 100).toFixed(1)
                        : '0';
                      
                      const displayStatus = getDisplayStatus(email.email, email.status);
                      const hasPendingChange = pendingStatusChanges.has(email.email);
                      
                      return (
                        <tr key={index} className={hasPendingChange ? 'has-pending-change' : ''}>
                          <td>
                            <input 
                              type="checkbox"
                              checked={selectedEmails.has(email.email)}
                              onChange={() => handleSelectEmail(email.email)}
                            />
                          </td>
                          <td className="email-cell">{email.email}</td>
                          <td>
                            <span className={`status-badge ${displayStatus.toLowerCase()}`}>
                              {displayStatus}
                              {hasPendingChange && <span className="pending-indicator">*</span>}
                            </span>
                          </td>
                          <td>
                            <div className="mailing-lists-cell">
                              {email.mailingLists && email.mailingLists.length > 0 ? (
                                email.mailingLists.map((list, idx) => (
                                  <span key={idx} className="mailing-list-tag">
                                    {list}
                                  </span>
                                ))
                              ) : (
                                <span className="no-lists">None</span>
                              )}
                            </div>
                          </td>
                          <td>{email.createdAt ? new Date(email.createdAt).toLocaleDateString() : 'N/A'}</td>
                          <td>{new Date(email.lastUpdated).toLocaleDateString()}</td>
                          <td className="metric-cell">{email.engagementStats?.totalOpened || 0}</td>
                          <td className="metric-cell">{email.engagementStats?.totalClicked || 0}</td>
                          <td className="metric-cell">{email.engagementStats?.totalNewsletters || 0}</td>
                          <td className="metric-cell engagement-cell">
                            <div className="engagement-rate">
                              <span>{engagementRate}%</span>
                              <div className="engagement-bar">
                                <div 
                                  className="engagement-fill" 
                                  style={{width: `${engagementRate}%`}}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              {mailingLists[0].emails.length === 0 && (
                <div className="empty-state">
                  <p>No subscribers found. Add your first subscriber!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <p>Loading mailing list data...</p>
            </div>
          )}
        </div>
      )}

      {activeSection === 'newsletters' && (
        <div className="section-container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Newsletters</h2>
              <p className="section-description">Manage and track all your email newsletters</p>
            </div>
            <div className="section-actions">
              <button className="btn btn-secondary" onClick={fetchNewsletters}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              <button className="btn btn-primary" onClick={() => showNotification('Newsletter creation coming soon!', 'info')}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Newsletter
              </button>
            </div>
          </div>

          {/* Production Newsletters */}
          <div className="newsletter-category">
            <h3>Production Newsletters ({productionNewsletters.length})</h3>
            <div className="newsletter-grid">
              {productionNewsletters.map((newsletter) => (
                <div key={newsletter.id} className="newsletter-card production">
                  <div className="newsletter-header">
                    <h4>{newsletter.metadata?.title || newsletter.id}</h4>
                    <span className="status production">Production</span>
                  </div>
                  
                  <div className="newsletter-info">
                    <p className="newsletter-id">ID: {newsletter.id}</p>
                    <p className="newsletter-date">
                      Created: {new Date(newsletter.created).toLocaleDateString()}
                    </p>
                    <p className="mailing-list">Mailing List: Default</p>
                  </div>

                  <div className="newsletter-actions">
                    <button 
                      onClick={() => handleViewHtml(newsletter.id)}
                      className="btn btn-sm"
                      disabled={loadingPreviewIds.has(newsletter.id)}
                    >
                      {loadingPreviewIds.has(newsletter.id) ? 'Loading...' : 'View HTML'}
                    </button>
                    {newsletter.pdfUrl && (
                      <a 
                        href={newsletter.pdfUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                      >
                        View PDF
                      </a>
                    )}
                    <button 
                      className="btn btn-sm btn-info"
                      onClick={() => handleViewAnalytics(newsletter.id)}
                    >
                      Analytics
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Draft Newsletters */}
          <div className="newsletter-category">
            <h3>Draft Newsletters ({draftNewsletters.length})</h3>
            <div className="newsletter-grid">
              {draftNewsletters.map((newsletter) => (
                <div key={newsletter.id} className="newsletter-card draft">
                  <div className="newsletter-header">
                    <h4>{newsletter.metadata?.title || newsletter.id}</h4>
                    <span className="status draft">Draft</span>
                  </div>
                  
                  <div className="newsletter-info">
                    <p className="newsletter-id">ID: {newsletter.id}</p>
                    <p className="newsletter-date">
                      Created: {new Date(newsletter.created).toLocaleDateString()}
                    </p>
                    <p className="mailing-list">Mailing List: Default</p>
                  </div>

                  <div className="newsletter-actions">
                    <button 
                      onClick={() => handleViewHtml(newsletter.id)}
                      className="btn btn-sm"
                      disabled={loadingPreviewIds.has(newsletter.id)}
                    >
                      {loadingPreviewIds.has(newsletter.id) ? 'Loading...' : 'View HTML'}
                    </button>
                    {newsletter.pdfUrl && (
                      <a 
                        href={newsletter.pdfUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                      >
                        View PDF
                      </a>
                    )}
                    <button 
                      className="btn btn-sm btn-success" 
                      onClick={() => showNotification('Move to production functionality coming soon!', 'info')}
                    >
                      Move to Prod
                    </button>
                    <button
                      onClick={() => handleDeleteNewsletter(newsletter.id, newsletter.isProd)}
                      className="btn btn-sm btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {newsletters.length === 0 && (
            <div className="empty-state">
              <p>No newsletters yet. Create your first newsletter!</p>
            </div>
          )}
        </div>
      )}

      {/* Subscriber Management Modal */}
      {showSubscriberModal && selectedMailingList && (
        <div className="modal-overlay" onClick={handleCloseSubscriberModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Subscribers - {selectedMailingList.name}</h2>
              <button className="modal-close" onClick={handleCloseSubscriberModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="subscriber-stats">
                <p><strong>Total Subscribers:</strong> {selectedMailingList.subscriberCount}</p>
                <p><strong>Last Updated:</strong> {new Date(selectedMailingList.lastUpdated).toLocaleDateString()}</p>
              </div>

              <div className="subscriber-actions">
                <button className="btn btn-primary" onClick={() => setShowAddSubscriberModal(true)}>
                  + Add New Subscriber
                </button>
                <button className="btn btn-secondary" onClick={() => showNotification('Export functionality coming soon!', 'info')}>
                  Export List
                </button>
              </div>

              <div className="subscriber-list">
                <h3>Subscribers ({selectedMailingList.emails?.length || 0})</h3>
                {selectedMailingList.emails && selectedMailingList.emails.length > 0 ? (
                  <div className="subscriber-table">
                    <div className="table-header">
                      <div className="table-col">Email</div>
                      <div className="table-col">Status</div>
                      <div className="table-col">Last Updated</div>
                      <div className="table-col">Actions</div>
                    </div>
                    {selectedMailingList.emails.map((email, index) => (
                      <div key={index} className="table-row">
                        <div className="table-col email-col">{email.email}</div>
                        <div className="table-col">
                          <span className={`status-badge ${email.status.toLowerCase()}`}>
                            {email.status}
                          </span>
                        </div>
                        <div className="table-col">
                          {new Date(email.lastUpdated).toLocaleDateString()}
                        </div>
                        <div className="table-col">
                          {email.status === 'SUBSCRIBED' ? (
                            <button 
                              className="btn btn-sm btn-warning"
                              onClick={() => handleUnsubscribeEmail(email.email)}
                            >
                              Unsubscribe
                            </button>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-subscriber-list">
                    <p>No subscribers found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Mailing List Modal */}
      {showCreateListModal && (
        <div className="modal-overlay" onClick={() => setShowCreateListModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Mailing List</h2>
              <button className="modal-close" onClick={() => setShowCreateListModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="listName">Mailing List Name:</label>
                <input
                  type="text"
                  id="listName"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g., Weekly Newsletter, Product Updates"
                  className="form-input"
                />
              </div>

              <div className="selected-emails-info">
                <p><strong>Selected Emails ({selectedEmails.size}):</strong></p>
                {selectedEmails.size === 0 ? (
                  <p className="no-selection">Please select emails from the table above first.</p>
                ) : (
                  <div className="email-list">
                    {Array.from(selectedEmails).slice(0, 10).map(email => (
                      <span key={email} className="selected-email-tag">{email}</span>
                    ))}
                    {selectedEmails.size > 10 && <span>...and {selectedEmails.size - 10} more</span>}
                  </div>
                )}
              </div>

              <div className="bulk-add-section">
                <h4>Or Add Emails in Bulk:</h4>
                <textarea
                  placeholder="Paste email addresses here (one per line or comma-separated)&#10;example@email.com&#10;another@email.com"
                  rows={4}
                  className="bulk-email-textarea"
                  onBlur={(e) => {
                    const emails = e.target.value
                      .split(/[\n,]/)
                      .map(email => email.trim())
                      .filter(email => email && email.includes('@'));
                    
                    if (emails.length > 0) {
                      const newSelected = new Set(Array.from(selectedEmails).concat(emails));
                      setSelectedEmails(newSelected);
                      e.target.value = '';
                    }
                  }}
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleCreateMailingList}
                  disabled={!newListName.trim() || selectedEmails.size === 0 || isCreatingList}
                >
                  {isCreatingList ? 'Creating...' : `Create List (${selectedEmails.size} emails)`}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowCreateListModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Mailing Lists Modal */}
      {showManageListsModal && (
        <div className="modal-overlay" onClick={() => setShowManageListsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedListAction === 'add' ? 'Add to Mailing List' : 'Remove from Mailing List'}</h2>
              <button className="modal-close" onClick={() => setShowManageListsModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="selected-emails-info">
                <p><strong>{selectedEmails.size} email(s) selected</strong></p>
                <div className="email-list">
                  {Array.from(selectedEmails).slice(0, 5).map(email => (
                    <span key={email} className="selected-email-tag">{email}</span>
                  ))}
                  {selectedEmails.size > 5 && <span>...and {selectedEmails.size - 5} more</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="targetList">
                  {selectedListAction === 'add' ? 'Add to list:' : 'Remove from list:'}
                </label>
                <select
                  id="targetList"
                  value={targetMailingList}
                  onChange={(e) => setTargetMailingList(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a mailing list...</option>
                  {Array.from(new Set(
                    mailingLists[0]?.emails?.flatMap(email => email.mailingLists || []) || []
                  )).sort().map(listName => (
                    <option key={listName} value={listName}>{listName}</option>
                  ))}
                </select>
              </div>

              {selectedListAction === 'add' && (
                <div className="form-group">
                  <label htmlFor="newListForAdd">Or create a new list:</label>
                  <input
                    type="text"
                    id="newListForAdd"
                    placeholder="Enter new list name"
                    className="form-input"
                    onChange={(e) => {
                      if (e.target.value) {
                        setTargetMailingList(e.target.value);
                      }
                    }}
                  />
                </div>
              )}

              <div className="modal-actions">
                <button 
                  className={`btn ${selectedListAction === 'add' ? 'btn-primary' : 'btn-danger'}`}
                  onClick={handleManageMailingLists}
                  disabled={!targetMailingList || isManagingLists}
                >
                  {isManagingLists 
                    ? (selectedListAction === 'add' ? 'Adding...' : 'Removing...') 
                    : (selectedListAction === 'add' ? 'Add to List' : 'Remove from List')
                  }
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowManageListsModal(false);
                    setTargetMailingList('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Subscriber Modal */}
      {showAddSubscriberModal && (
        <div className="modal-overlay" onClick={() => setShowAddSubscriberModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Subscriber</h2>
              <button className="modal-close" onClick={() => setShowAddSubscriberModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="newEmail">Email Address:</label>
                <input
                  type="email"
                  id="newEmail"
                  value={newSubscriberEmail}
                  onChange={(e) => setNewSubscriberEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="form-input"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddSubscriber();
                    }
                  }}
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleAddSubscriber}
                  disabled={!newSubscriberEmail || !newSubscriberEmail.includes('@') || isAddingSubscriber}
                >
                  {isAddingSubscriber ? 'Adding...' : 'Add Subscriber'}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowAddSubscriberModal(false);
                    setNewSubscriberEmail('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HTML Preview Modal */}
      {showHtmlPreviewModal && (
        <div className="modal-overlay" onClick={() => setShowHtmlPreviewModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Newsletter HTML Preview</h3>
              <button className="modal-close" onClick={() => setShowHtmlPreviewModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {loadingPreviewIds.size > 0 ? (
                <div className="loading-spinner">Loading HTML preview...</div>
              ) : (
                <div className="html-preview-container">
                  <iframe
                    srcDoc={previewHtmlContent}
                    style={{
                      width: '100%',
                      height: '80vh',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    sandbox="allow-same-origin"
                    title="Newsletter HTML Preview"
                  />
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowHtmlPreviewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedNewsletterAnalytics && (
        <div className="modal-overlay" onClick={() => setShowAnalyticsModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Newsletter Analytics - {selectedNewsletterAnalytics.newsletterId}</h3>
              <button className="modal-close" onClick={() => setShowAnalyticsModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {isLoadingAnalytics ? (
                <div className="loading-spinner">Loading analytics...</div>
              ) : (
                <div className="analytics-details">
                  <div className="analytics-overview">
                    <div className="analytics-stat-grid">
                      <div className="analytics-stat-card">
                        <div className="stat-label">Total Recipients</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.total.toLocaleString()}</div>
                      </div>
                      
                      <div className="analytics-stat-card">
                        <div className="stat-label">Delivered</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.delivered.toLocaleString()}</div>
                        <div className="stat-percentage">{selectedNewsletterAnalytics.stats.deliveryRate}%</div>
                      </div>
                      
                      <div className="analytics-stat-card">
                        <div className="stat-label">Opened</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.opened.toLocaleString()}</div>
                        <div className="stat-percentage">{selectedNewsletterAnalytics.stats.openRate}%</div>
                      </div>
                      
                      <div className="analytics-stat-card">
                        <div className="stat-label">Clicked</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.clicked.toLocaleString()}</div>
                        <div className="stat-percentage">{selectedNewsletterAnalytics.stats.clickRate}%</div>
                      </div>
                      
                      <div className="analytics-stat-card">
                        <div className="stat-label">Viewed</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.viewed.toLocaleString()}</div>
                      </div>
                      
                      <div className="analytics-stat-card">
                        <div className="stat-label">Bounced</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.bounced.toLocaleString()}</div>
                        <div className="stat-percentage">{selectedNewsletterAnalytics.stats.bounceRate}%</div>
                      </div>
                      
                      <div className="analytics-stat-card">
                        <div className="stat-label">Complaints</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.complaints.toLocaleString()}</div>
                      </div>
                      
                      <div className="analytics-stat-card">
                        <div className="stat-label">Sent</div>
                        <div className="stat-value">{selectedNewsletterAnalytics.stats.sent.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                  
                  {selectedNewsletterAnalytics.recipients && selectedNewsletterAnalytics.recipients.length > 0 && (
                    <div className="analytics-recipients">
                      <h4>Individual Recipient Status</h4>
                      <div className="recipients-table-container">
                        <table className="recipients-table">
                          <thead>
                            <tr>
                              <th>Email</th>
                              <th>Status</th>
                              <th>Sent At</th>
                              <th>Delivered At</th>
                              <th>Opened At</th>
                              <th>Clicked At</th>
                              <th>Viewed At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedNewsletterAnalytics.recipients.slice(0, 100).map((recipient, index) => (
                              <tr key={index}>
                                <td>{recipient.email}</td>
                                <td>
                                  <span className={`status-badge ${recipient.status?.toLowerCase()}`}>
                                    {recipient.status}
                                  </span>
                                </td>
                                <td>{recipient.sentAt ? new Date(recipient.sentAt).toLocaleString() : '-'}</td>
                                <td>{recipient.deliveredAt ? new Date(recipient.deliveredAt).toLocaleString() : '-'}</td>
                                <td>{recipient.openedAt ? new Date(recipient.openedAt).toLocaleString() : '-'}</td>
                                <td>{recipient.clickedAt ? new Date(recipient.clickedAt).toLocaleString() : '-'}</td>
                                <td>{recipient.viewedAt ? new Date(recipient.viewedAt).toLocaleString() : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {selectedNewsletterAnalytics.recipients.length > 100 && (
                        <p className="recipients-note">Showing first 100 recipients of {selectedNewsletterAnalytics.recipients.length}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowAnalyticsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterList;